import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { Calendar } from "primereact/calendar";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { CreateTargetAction } from "../../actions/CreateTargetAction";

// Custom debounce hook
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  return debouncedCallback;
}

const CreateTarget = () => {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const [targetVisible, setTargetVisible] = useState(false);
  const [categoryVisible, setCategoryVisible] = useState(false);
  const [regionList, setRegionList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [productTargetData, setProductTargetData] = useState([]);
  const [categoryTargetData, setCategoryTargetData] = useState([]);
  const [globalFilterProduct, setGlobalFilterProduct] = useState("");
  const [globalFilterCategory, setGlobalFilterCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [productPagination, setProductPagination] = useState({
    first: 0,
    rows: 10,
    page: 0,
    totalRecords: 0,
    search: "",
    sortField: null,
    sortOrder: null,
  });
  const [categoryPagination, setCategoryPagination] = useState({
    first: 0,
    rows: 10,
    page: 0,
    totalRecords: 0,
    search: "",
    sortField: null,
    sortOrder: null,
  });
  const [formHeading, setFormHeading] = useState("Create Product Target");
  const [buttonLabel, setButtonLabel] = useState("Submit");

  const pageSizeOptions = [5, 10, 20, 50, 100];

  const initialForm = {
    selectedRegion: [],
    selectedState: [],
    selectedDistrict: [],
    selectedCity: [],
    selectedProducts: [],
    selectedCategory: [],
    targetName: "",
    targetDateTime: null,
    formError: {},
  };

  const [targetForm, setTargetForm] = useState(initialForm);
  const [categoryForm, setCategoryForm] = useState(initialForm);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(CreateTargetAction.getAllRegions()).then((data) => setRegionList(data || [])),
      dispatch(CreateTargetAction.getAllProductList()).then((data) => {
        if (Array.isArray(data)) {
          const transformed = data.map((item) => ({
            label: item.ProductName,
            value: item.ProductId,
          }));
          setProductList(transformed);
        }
      }),
      dispatch(CreateTargetAction.getAllCategory()).then((data) => {
        if (Array.isArray(data)) {
          const transformed = data.map((item) => ({
            label: item.label,
            value: item.value,
          }));
          setCategoryList(transformed);
        }
      }),
      fetchProductTargetData(),
      fetchCategoryTargetData(),
    ]).finally(() => setLoading(false));
  }, [
    dispatch,
    productPagination.page,
    productPagination.rows,
    productPagination.search,
    productPagination.sortField,
    productPagination.sortOrder,
    categoryPagination.page,
    categoryPagination.rows,
    categoryPagination.search,
    categoryPagination.sortField,
    categoryPagination.sortOrder,
  ]);

  const fetchProductTargetData = () => {
    setLoading(true);
    const request = {
      Search: productPagination.search,
      pageNumber: productPagination.page + 1,
      pageSize: productPagination.rows,
      sortField: productPagination.sortField,
      sortOrder: productPagination.sortOrder === 1 ? "ASC" : productPagination.sortOrder === -1 ? "DESC" : null,
    };
    return dispatch(CreateTargetAction.getProductTargetData(request))
      .then((response) => {
        if (response.ResponseCode === "200") {
          setProductTargetData(response.ResponseData || []);
          setProductPagination((prev) => ({
            ...prev,
            totalRecords: response.ResponseData[0]?.TotalRecords || 0,
          }));
        } else {
          setProductTargetData([]);
          setProductPagination((prev) => ({
            ...prev,
            totalRecords: 0,
            first: 0,
          }));
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: response.ResponseMessage || "Failed to load product target data",
          });
        }
      })
      .catch((error) => {
        setProductTargetData([]);
        setProductPagination((prev) => ({
          ...prev,
          totalRecords: 0,
          first: 0,
        }));
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.message || "Failed to load product target data",
        });
      })
      .finally(() => setLoading(false));
  };

  const fetchCategoryTargetData = () => {
    setLoading(true);
    const request = {
      Search: categoryPagination.search,
      pageNumber: categoryPagination.page + 1,
      pageSize: categoryPagination.rows,
      sortField: categoryPagination.sortField,
      sortOrder: categoryPagination.sortOrder === 1 ? "ASC" : categoryPagination.sortOrder === -1 ? "DESC" : null,
    };
    return dispatch(CreateTargetAction.getCategoryTargetData(request))
      .then((response) => {
        if (response.ResponseCode === "200") {
          setCategoryTargetData(response.ResponseData || []);
          setCategoryPagination((prev) => ({
            ...prev,
            totalRecords: response.ResponseData[0]?.TotalRecords || 0,
          }));
        } else {
          setCategoryTargetData([]);
          setCategoryPagination((prev) => ({
            ...prev,
            totalRecords: 0,
            first: 0,
          }));
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: response.ResponseMessage || "Failed to load category target data",
          });
        }
      })
      .catch((error) => {
        setCategoryTargetData([]);
        setCategoryPagination((prev) => ({
          ...prev,
          totalRecords: 0,
          first: 0,
        }));
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.message || "Failed to load category target data",
        });
      })
      .finally(() => setLoading(false));
  };

  const loadStates = (regionIds, formType) => {
    const setForm = formType === "target" ? setTargetForm : setCategoryForm;
    if (regionIds?.length > 0) {
      const param = regionIds.join(",");
      setLoading(true);
      dispatch(CreateTargetAction.getStateDDL(param))
        .then((data) => {
          setStateList(data || []);
          setForm((prev) => ({
            ...prev,
            selectedState: [],
            selectedDistrict: [],
            selectedCity: [],
          }));
          setDistrictList([]);
          setCityList([]);
        })
        .finally(() => setLoading(false));
    } else {
      setStateList([]);
      setDistrictList([]);
      setCityList([]);
      setForm((prev) => ({
        ...prev,
        selectedState: [],
        selectedDistrict: [],
        selectedCity: [],
      }));
    }
  };

  const loadDistricts = (stateIds, formType) => {
    const setForm = formType === "target" ? setTargetForm : setCategoryForm;
    if (stateIds?.length > 0) {
      const param = stateIds.join(",");
      setLoading(true);
      dispatch(CreateTargetAction.getDistrictDDL(param))
        .then((data) => {
          setDistrictList(data || []);
          setForm((prev) => ({ ...prev, selectedDistrict: [], selectedCity: [] }));
          setCityList([]);
        })
        .finally(() => setLoading(false));
    } else {
      setDistrictList([]);
      setCityList([]);
      setForm((prev) => ({ ...prev, selectedDistrict: [], selectedCity: [] }));
    }
  };

  const loadCities = (districtIds, formType) => {
    const setForm = formType === "target" ? setTargetForm : setCategoryForm;
    if (districtIds?.length > 0) {
      const param = districtIds.join(",");
      setLoading(true);
      dispatch(CreateTargetAction.getTownDDL(param))
        .then((data) => {
          setCityList(data || []);
          setForm((prev) => ({ ...prev, selectedCity: [] }));
        })
        .finally(() => setLoading(false));
    } else {
      setCityList([]);
      setForm((prev) => ({ ...prev, selectedCity: [] }));
    }
  };

  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const validateForm = (form, formType) => {
    const errors = {};
    if (!form.selectedRegion.length) errors.selectedRegion = "Region is required";
    if (!form.selectedState.length) errors.selectedState = "State is required";
    if (!form.selectedDistrict.length) errors.selected  = "District is required";
    if (!form.selectedCity.length) errors.selectedCity = "City/Town is required";
    if (formType === "target" && !form.selectedProducts.length)
      errors.selectedProducts = "At least one product is required";
    if (formType === "category" && !form.selectedCategory.length)
      errors.selectedCategory = "At least one category is required";
    if (!form.targetDateTime || !(form.targetDateTime instanceof Date) || isNaN(form.targetDateTime.getTime()))
      errors.targetDateTime = "Month and Year are required";
    return errors;
  };

  const buildPayload = (form, formType) => {
    const formattedDate = formatDate(form.targetDateTime);
    if (!formattedDate) return { payload: [], error: "Invalid date format" };

    const uniqueKeys = new Set();
    const payload = [];

    form.selectedRegion.forEach((regionId) => {
      form.selectedState.forEach((stateId) => {
        form.selectedDistrict.forEach((districtId) => {
          form.selectedCity.forEach((townId) => {
            const categories = formType === "category" && form.selectedCategory?.length ? form.selectedCategory : [null];
            const products = formType === "target" && form.selectedProducts?.length ? form.selectedProducts : [null];

            categories.forEach((categoryId) => {
              products.forEach((productId) => {
                const key = `${regionId}_${stateId}_${districtId}_${townId}_${categoryId || "x"}_${productId || "x"}`;
                if (!uniqueKeys.has(key)) {
                  uniqueKeys.add(key);
                  const record = {
                    regionId,
                    stateId,
                    districtId,
                    townId,
                    marketName: form.targetName.trim(),
                    TargeteTime: formattedDate,
                  };
                  if (categoryId) record.categoryId = categoryId;
                  if (productId) record.productId = productId;
                  payload.push(record);
                }
              });
            });
          });
        });
      });
    });

    return { payload, error: payload.length === 0 ? "No unique targets to submit" : null };
  };

  const handleSubmit = async (event, formType) => {
    event.preventDefault();
    const form = formType === "target" ? targetForm : categoryForm;
    const setForm = formType === "target" ? setTargetForm : setCategoryForm;
    const submitApi = formType === "target" ? CreateTargetAction.submitProductTarget : CreateTargetAction.submitCategoryTarget;

    const errors = validateForm(form, formType);
    setForm((prev) => ({ ...prev, formError: errors }));

    if (Object.keys(errors).length > 0) {
      toast.current.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please fill all required fields",
      });
      return;
    }

    const { payload, error } = buildPayload(form, formType);
    if (error) {
      toast.current.show({
        severity: "warn",
        summary: "Error",
        detail: error,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await dispatch(submitApi(payload));
      if (response.ResponseCode === "200") {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `${formType === "target" ? "Product" : "Category"} Target Created Successfully`,
        });
        resetForm(formType);
        setTargetVisible(formType === "target" ? false : targetVisible);
        setCategoryVisible(formType === "category" ? false : categoryVisible);
        formType === "target" ? fetchProductTargetData() : fetchCategoryTargetData();
      } else {
        throw new Error(response.ResponseMessage || "Submission failed");
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message || `Failed to create ${formType} target`,
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = (formType) => {
    if (formType === "target") {
      setTargetForm(initialForm);
    } else {
      setCategoryForm(initialForm);
    }
    setStateList([]);
    setDistrictList([]);
    setCityList([]);
  };

  const onProductPage = (event) => {
    setLoading(true);
    setProductPagination((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page,
    }));
  };

  const onCategoryPage = (event) => {
    setLoading(true);
    setCategoryPagination((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page,
    }));
  };

  const onProductSort = (event) => {
    setLoading(true);
    setProductPagination((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
      first: 0,
      page: 0,
    }));
  };

  const onCategorySort = (event) => {
    setLoading(true);
    setCategoryPagination((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
      first: 0,
      page: 0,
    }));
  };

  const handleProductSearch = useDebounce((value) => {
    setGlobalFilterProduct(value);
    setProductPagination((prev) => ({
      ...prev,
      search: value,
      first: 0,
      page: 0,
    }));
  }, 500);

  const handleCategorySearch = useDebounce((value) => {
    setGlobalFilterCategory(value);
    setCategoryPagination((prev) => ({
      ...prev,
      search: value,
      first: 0,
      page: 0,
    }));
  }, 500);

  const onProductSearchChange = (e) => {
    const value = e.target.value || "";
    setGlobalFilterProduct(value);
    handleProductSearch(value);
  };

  const onCategorySearchChange = (e) => {
    const value = e.target.value || "";
    setGlobalFilterCategory(value);
    handleCategorySearch(value);
  };

  const handleChange = (event, formType) => {
    const setForm = formType === "target" ? setTargetForm : setCategoryForm;
    const { name, value } = event.target;
    setForm((prev) => {
      const errors = { ...prev.formError };
      errors[name] = validateField(name, value, formType);
      return { ...prev, [name]: value, formError: errors };
    });

    if (name === "selectedRegion") {
      loadStates(value, formType);
    } else if (name === "selectedState") {
      loadDistricts(value, formType);
    } else if (name === "selectedDistrict") {
      loadCities(value, formType);
    }
  };

  const validateField = (name, value, formType) => {
    switch (name) {
      case "selectedRegion":
        return value.length < 1 ? "Region is required" : "";
      case "selectedState":
        return value.length < 1 ? "State is required" : "";
      case "selectedDistrict":
        return value.length < 1 ? "District is required" : "";
      case "selectedCity":
        return value.length < 1 ? "City/Town is required" : "";
      case "selectedProducts":
        return formType === "target" && value.length < 1 ? "At least one product is required" : "";
      case "selectedCategory":
        return formType === "category" && value.length < 1 ? "At least one category is required" : "";
      case "targetDateTime":
        return !value || !(value instanceof Date) || isNaN(value.getTime()) ? "Month and Year are required" : "";
      default:
        return "";
    }
  };

  const openDialog = (formType) => {
    setFormHeading(formType === "target" ? "Create Product Target" : "Create Category Target");
    setButtonLabel("Submit");
    if (formType === "target") {
      setTargetVisible(true);
    } else {
      setCategoryVisible(true);
    }
  };

  const onHide = (formType) => {
    resetForm(formType);
    if (formType === "target") {
      setTargetVisible(false);
    } else {
      setCategoryVisible(false);
    }
  };

  const renderForm = (formType) => {
    const form = formType === "target" ? targetForm : categoryForm;
    return (
      <form onSubmit={(e) => handleSubmit(e, formType)}>
        <div className="row">
          <div className="col-md-6 pt-2">
            <MultiSelect
              value={form.selectedRegion}
              options={regionList}
              onChange={(e) => handleChange({ target: { name: "selectedRegion", value: e.value } }, formType)}
              optionLabel="label"
              optionValue="value"
              placeholder="Select Region(s)*"
              className="w-100"
              filter
            />
            {form.formError.selectedRegion && <div className="error">{form.formError.selectedRegion}</div>}
          </div>
          <div className="col-md-6 pt-2">
            <MultiSelect
              value={form.selectedState}
              options={stateList}
              onChange={(e) => handleChange({ target: { name: "selectedState", value: e.value } }, formType)}
              optionLabel="label"
              optionValue="value"
              placeholder="Select State(s)*"
              className="w-100"
              filter
            />
            {form.formError.selectedState && <div className="error">{form.formError.selectedState}</div>}
          </div>
          <div className="col-md-6 pt-2">
            <MultiSelect
              value={form.selectedDistrict}
              options={districtList}
              onChange={(e) => handleChange({ target: { name: "selectedDistrict", value: e.value } }, formType)}
              optionLabel="label"
              optionValue="value"
              placeholder="Select District(s)*"
              className="w-100"
              filter
            />
            {form.formError.selectedDistrict && <div className="error">{form.formError.selectedDistrict}</div>}
          </div>
          <div className="col-md-6 pt-2">
            <MultiSelect
              value={form.selectedCity}
              options={cityList}
              onChange={(e) => handleChange({ target: { name: "selectedCity", value: e.value } }, formType)}
              optionLabel="label"
              optionValue="value"
              placeholder="Select City/Town(s)*"
              className="w-100"
              filter
            />
            {form.formError.selectedCity && <div className="error">{form.formError.selectedCity}</div>}
          </div>
          {formType === "category" && (
            <div className="col-md-6 pt-2">
              <MultiSelect
                value={form.selectedCategory}
                options={categoryList}
                onChange={(e) => handleChange({ target: { name: "selectedCategory", value: e.value } }, formType)}
                optionLabel="label"
                optionValue="value"
                placeholder="Select Category(s)*"
                className="w-100"
                filter
              />
              {form.formError.selectedCategory && <div className="error">{form.formError.selectedCategory}</div>}
            </div>
          )}
          {formType === "target" && (
            <div className="col-md-6 pt-2">
              <MultiSelect
                value={form.selectedProducts}
                options={productList}
                onChange={(e) => handleChange({ target: { name: "selectedProducts", value: e.value } }, formType)}
                optionLabel="label"
                optionValue="value"
                placeholder="Select Product(s)*"
                className="w-100"
                filter
              />
              {form.formError.selectedProducts && <div className="error">{form.formError.selectedProducts}</div>}
            </div>
          )}
          <div className="col-md-6 pt-2">
            <Calendar
              value={form.targetDateTime}
              onChange={(e) => handleChange({ target: { name: "targetDateTime", value: e.value } }, formType)}
              view="month"
              dateFormat="mm/yy"
              placeholder="Select Month and Year"
              className="w-100"
              showIcon
              yearRange="2000:2100"
            />
            {form.formError.targetDateTime && <div className="error">{form.formError.targetDateTime}</div>}
          </div>
          <div className="col-md-12 text-center pt-3">
            <Button label={buttonLabel} icon="pi pi-check" />
          </div>
          <div className="col-md-12 pt-2">
            Note: Fields marked as (*) are required
          </div>
        </div>
      </form>
    );
  };

  const productHeader = (
    <div className="table-header row">
      <div className="col-md-6">
        <h4 className="m-0">Product Targets</h4>
      </div>
      <div className="col-md-6 d-flex justify-content-end">
        <span className="p-input-icon-left mr-2">
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={globalFilterProduct}
            onChange={onProductSearchChange}
            placeholder="Search Products"
          />
        </span>
        <Button
          style={{ marginLeft: "10px" }}
          className="mr-2"
          icon="pi pi-plus"
          label="Add Product Target"
          onClick={() => openDialog("target")}
        />
      </div>
    </div>
  );

  const categoryHeader = (
    <div className="table-header row">
      <div className="col-md-6">
        <h4 className="m-0">Category Targets</h4>
      </div>
      <div className="col-md-6 d-flex justify-content-end">
        <span className="p-input-icon-left mr-2">
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={globalFilterCategory}
            onChange={onCategorySearchChange}
            placeholder="Search Categories"
          />
        </span>
        <Button
         style={{ marginLeft: "10px" }}
          className="mr-2"
          icon="pi pi-plus"
          label="Add Category Target"
          onClick={() => openDialog("category")}
        />
      </div>
    </div>
  );

  return (
    <div className="datatable-filter-demo">
      <Toast ref={toast} />
      <div className="card">
        {loading && (
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }}>
            <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          </div>
        )}
        <TabView>
          <TabPanel header="Product Targets">
            <DataTable
              value={productTargetData}
              stripedRows
              scrollable
              scrollHeight="500px"
              responsiveLayout="scroll"
              header={productHeader}
              emptyMessage="No product targets found."
              sortMode="single"
              removableSort
              paginator
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
              rows={productPagination.rows}
              rowsPerPageOptions={pageSizeOptions}
              totalRecords={productPagination.totalRecords}
              lazy
              first={productPagination.first}
              onPage={onProductPage}
              onSort={onProductSort}
              sortField={productPagination.sortField}
              sortOrder={productPagination.sortOrder}
              className="p-datatable-gridlines"
              loading={loading}
            >
              <Column field="ProductName" header="Product Name" sortable style={{ minWidth: "150px" }} />
              <Column field="RegionName" header="Region" sortable style={{ minWidth: "150px" }} />
              <Column field="State" header="State" sortable style={{ minWidth: "150px" }} />
              <Column field="District" header="District" sortable style={{ minWidth: "150px" }} />
              <Column field="TownName" header="Town" sortable style={{ minWidth: "150px" }} />
              <Column field="TargetedTime" header="Targeted Time" sortable style={{ minWidth: "150px" }} />
            </DataTable>
          </TabPanel>
          <TabPanel header="Category Targets">
            <DataTable
              value={categoryTargetData}
              stripedRows
              scrollable
              scrollHeight="500px"
              responsiveLayout="scroll"
              header={categoryHeader}
              emptyMessage="No category targets found."
              sortMode="single"
              removableSort
              paginator
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
              rows={categoryPagination.rows}
              rowsPerPageOptions={pageSizeOptions}
              totalRecords={categoryPagination.totalRecords}
              lazy
              first={categoryPagination.first}
              onPage={onCategoryPage}
              onSort={onCategorySort}
              sortField={categoryPagination.sortField}
              sortOrder={categoryPagination.sortOrder}
              className="p-datatable-gridlines"
              loading={loading}
            >
              <Column field="CategoryName" header="Category Name" sortable style={{ minWidth: "150px" }} />
              <Column field="RegionName" header="Region" sortable style={{ minWidth: "150px" }} />
              <Column field="State" header="State" sortable style={{ minWidth: "150px" }} />
              <Column field="District" header="District" sortable style={{ minWidth: "150px" }} />
              <Column field="TownName" header="Town" sortable style={{ minWidth: "150px" }} />
              <Column field="TargetedTime" header="Targeted Time" sortable style={{ minWidth: "150px" }} />
            </DataTable>
          </TabPanel>
        </TabView>

        <Dialog
          header={formHeading}
          visible={targetVisible}
          className="popup1"
          style={{ width: "50vw", overflowX: "auto" }}
          onHide={() => onHide("target")}
          modal
        >
          {renderForm("target")}
        </Dialog>

        <Dialog
          header={formHeading}
          visible={categoryVisible}
          className="popup1"
          style={{ width: "50vw", overflowX: "auto" }}
          onHide={() => onHide("category")}
          modal
        >
          {renderForm("category")}
        </Dialog>
      </div>
    </div>
  );
};

export default CreateTarget;